import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchBooksInfinite } from '@/features/library/hooks/useSearchBooksInfinite';
import type { Book } from '@/features/library/api/books';
import BookCard from './BookCard';

interface RelatedBooksProps {
  currentBookId: number;
  topic: string;
  author?: string;
}

const RelatedBooks: React.FC<RelatedBooksProps> = ({ currentBookId, topic }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const { 
    books: allBooks, 
    isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    fetchNextPage 
  } = useSearchBooksInfinite(
    { topic },
    { enabled: !!topic && isNearViewport, limit: 10 }
  );

  // Filter out current book
  const relatedBooks = React.useMemo(
    () => allBooks.filter((b: Book) => b.book_id !== currentBookId),
    [allBooks, currentBookId]
  );

  // Handle scroll to load more (RTL: scrollLeft starts at 0 and goes negative)
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // In RTL, scrollLeft is 0 at start (right side) and becomes negative as you scroll left
    const maxScroll = scrollWidth - clientWidth;
    const currentScroll = Math.abs(scrollLeft);
    
    // Load more when scrolled to 80% of the content (near the left end in RTL)
    if (currentScroll >= maxScroll * 0.8) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Keep all hooks above this branch so the hook order is stable between renders.
  if (!isNearViewport) {
    return <div ref={sectionRef} className="h-px" aria-hidden="true" />;
  }

  if (isLoading) {
    return (
      <div className="mt-4 space-y-3">
        <h3 className="px-3 text-sm md:text-base font-bold text-foreground mb-2 font-persian">کتاب‌های مرتبط</h3>
        <div className="flex gap-3 px-3 pb-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex-shrink-0 w-32">
              <div className="w-full aspect-[0.62] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (relatedBooks.length === 0) return null;

  return (
    <div className="mt-4 space-y-3">
      <h3 className="px-3 text-sm md:text-base font-bold text-foreground mb-2 font-persian">کتاب‌های مرتبط</h3>
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-3 px-3 pb-3 hide-scrollbar"
      >
        {relatedBooks.map((book: Book, index: number) => (
          <div key={`${book.book_id}-${index}`} className="flex-shrink-0 w-32">
            <BookCard
              book={book}
              titleClassName="!text-sm"
              coverClassName="aspect-[0.62]"
              onClick={() => {
                const carouselBooks = relatedBooks.map((b) => ({
                  book_id: b.book_id,
                  title: b.title,
                  author: b.author,
                  cover_image_url: b.cover_image_url ?? null,
                  topic: b.topic,
                }));

                navigate(`/library/book`, {
                  state: {
                    bookId: book.book_id,
                    source: 'recommended',
                    books: carouselBooks,
                    initialIndex: index,
                  },
                });
              }}
            />
          </div>
        ))}
        {isFetchingNextPage && (
          <div className="flex-shrink-0 w-32">
            <div className="w-full aspect-[0.62] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RelatedBooks;
